import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Link2, Loader2, RefreshCw, ShieldAlert, Unlink, CheckCircle2, Eye, Heart, MessageCircle, Users } from "lucide-react";

const qs = (scope) => (scope ? `?tenant=${scope.tenant}&hotel=${scope.hotel}` : "");
const body = (scope) => (scope ? { tenant: scope.tenant, hotel: scope.hotel } : {});
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT"));

export default function MetaConnect({ scope, title, sub, onRead }) {
  const [st, setSt] = useState(null);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const s = await api.metaStatus(qs(scope)); setSt(s); if (s.connected) setSnap((await api.metaSnapshot(qs(scope))).snapshot); else setSnap(null); }
    catch (e) { toast.error(e.message); }
  }, [scope?.tenant, scope?.hotel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const connect = async () => { try { window.location.href = (await api.metaOAuthStart(qs(scope))).url; } catch (e) { toast.error(e.message); } };
  const read = async () => {
    setBusy(true);
    try { const r = await api.metaRead(body(scope)); setSnap(r.snapshot); toast.success("Facebook e Instagram letti: dati reali aggiornati."); await load(); onRead?.(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const select = async (page_id) => { try { setSt(await api.metaSelectPage({ ...body(scope), page_id })); } catch (e) { toast.error(e.message); } };
  const disconnect = async () => { try { await api.metaDisconnect(body(scope)); toast.message("Collegamento Meta rimosso."); await load(); } catch (e) { toast.error(e.message); } };

  if (!st) return null;
  return (
    <section className="mb-8" data-testid="meta-connect">
      <div className="bg-white rounded-xl border border-brand-gold/25 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold tracking-[0.2em] text-brand-gold uppercase">Collegamento ufficiale · gratuito</div>
            <div className="font-serif text-[22px] text-brand-ink leading-tight mt-1">{title || "Collega Facebook e Instagram"}</div>
            <p className="text-[13px] text-brand-muted mt-1 max-w-2xl">{sub || "Con l'autorizzazione di chi amministra la pagina, LEUTELIA legge dalla porta ufficiale di Meta: follower, post, copertura e interazioni reali. Nessun robot, nessun credito da consumare."}</p>
          </div>
          <Pill st={st} />
        </div>
        {!st.configured && <NotConfigured redirect={st.redirect_uri} />}
        {st.configured && !st.connected && (
          <Button onClick={connect} data-testid="meta-connect-btn" className="mt-4 h-10 rounded-lg bg-[#1877F2] hover:bg-[#145FCB] text-white text-[13px] font-semibold"><Facebook size={15} className="mr-2" /> Collega con Facebook</Button>
        )}
        {st.connected && (
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="meta-connected">
            {st.pages.length > 1 ? (
              <select data-testid="meta-page-select" value={st.selected || ""} onChange={(e) => select(e.target.value)} className="h-10 rounded-md border border-brand-gold/30 bg-white px-3 text-[13px]">
                <option value="">Scegli la pagina…</option>
                {st.pages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ig_username ? ` · @${p.ig_username}` : ""}</option>)}
              </select>
            ) : (
              <div className="text-[13.5px] text-brand-ink" data-testid="meta-page-name"><strong>{st.pages[0]?.name}</strong>{st.pages[0]?.ig_username ? <span className="text-brand-muted"> · @{st.pages[0].ig_username}</span> : <span className="text-brand-orange"> · nessun Instagram aziendale collegato alla pagina</span>}</div>
            )}
            <Button onClick={read} disabled={busy || !st.selected} data-testid="meta-read-btn" className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold">{busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RefreshCw size={15} className="mr-2" />} {snap ? "Rileggi ora" : "Leggi ora"}</Button>
            <button onClick={disconnect} data-testid="meta-disconnect-btn" className="text-[12px] text-brand-muted hover:text-brand-maroon inline-flex items-center gap-1"><Unlink size={13} /> Scollega</button>
            {st.last_read_at && <span className="text-[12px] text-brand-muted">Ultima lettura: {new Date(st.last_read_at).toLocaleString("it-IT")}</span>}
          </div>
        )}
      </div>
      {snap && <Snapshot snap={snap} />}
    </section>
  );
}

function Pill({ st }) {
  const c = !st.configured ? ["Non configurato", "text-brand-orange border-brand-orange/40", ShieldAlert] : st.connected ? ["Collegato", "text-emerald-700 border-emerald-600/30", CheckCircle2] : ["Da collegare", "text-brand-muted border-brand-gold/30", Link2];
  const I = c[2];
  return <span data-testid="meta-pill" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border bg-brand-cream/60 text-[11.5px] font-semibold ${c[1]}`}><I size={13} /> {c[0]}</span>;
}

function NotConfigured({ redirect }) {
  return (
    <div className="mt-4 rounded-lg border border-brand-orange/40 bg-[#FFF7EC] p-4 text-[12.5px] text-brand-ink/85 leading-relaxed" data-testid="meta-not-configured">
      <div className="font-semibold text-brand-ink">Per attivare il collegamento servono le chiavi dell'app Meta (una volta sola, gratuite).</div>
      <ol className="list-decimal pl-5 mt-2 space-y-1">
        <li>developers.facebook.com/apps → <strong>Crea app</strong> di tipo <strong>Business</strong>.</li>
        <li>Prodotto <strong>Facebook Login for Business</strong> → Impostazioni → URI di reindirizzamento OAuth validi: <code className="bg-white px-1 rounded border border-brand-gold/30 break-all">{redirect}</code></li>
        <li>Impostazioni → Di base: copia <strong>ID app</strong> e <strong>Chiave segreta</strong> → nelle variabili del server META_APP_ID e META_APP_SECRET.</li>
      </ol>
    </div>
  );
}

function Snapshot({ snap }) {
  const fb = snap.facebook || {}, ig = snap.instagram;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4" data-testid="meta-snapshot">
      <Channel icon={Facebook} color="#1877F2" name={fb.title || "Pagina Facebook"} url={fb.page_url} testid="meta-fb"
        stats={[[Users, "Follower", fb.followers], [Heart, "Mi piace", fb.likes], [Eye, "Persone raggiunte · 28 gg", fb.insights?.page_impressions_unique], [MessageCircle, "Interazioni · 28 gg", fb.insights?.page_post_engagements]]}
        posts={fb.latest_posts} error={fb.posts_error || fb.insights?._error} intro={fb.intro} />
      {ig ? (
        <Channel icon={Instagram} color="#C13584" name={ig.username ? `@${ig.username}` : "Instagram"} url={ig.username ? `https://www.instagram.com/${ig.username}/` : null} testid="meta-ig"
          stats={[[Users, "Follower", ig.followers], [Instagram, "Post pubblicati", ig.posts], [Eye, "Copertura · 28 gg", ig.insights?.reach], [MessageCircle, "Interazioni · 28 gg", ig.insights?.total_interactions]]}
          extra={[["Account coinvolti", ig.insights?.accounts_engaged], ["Visite al profilo", ig.insights?.profile_views], ["Click sul sito", ig.insights?.website_clicks]]}
          posts={ig.latest_posts} error={ig.error || ig.insights?._error} intro={ig.bio} />
      ) : (
        <div className="bg-white rounded-xl border border-brand-gold/25 p-5 text-[13px] text-brand-muted" data-testid="meta-ig-missing">Nessun account Instagram aziendale collegato alla pagina Facebook. Da Instagram: Impostazioni → Account → passa a account professionale e collega la pagina.</div>
      )}
    </div>
  );
}

function Channel({ icon: Icon, color, name, url, stats, extra, posts, error, intro, testid }) {
  return (
    <div className="bg-white rounded-xl border border-brand-gold/25 p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: color }}><Icon size={18} className="text-white" /></div>
        <div className="min-w-0"><div className="font-serif text-[20px] text-brand-ink leading-tight truncate">{name}</div>{url && <a href={url} target="_blank" rel="noreferrer" className="text-[12px] text-brand-green hover:underline break-all">{url}</a>}</div>
      </div>
      {intro && <p className="text-[12.5px] text-brand-muted mt-2 line-clamp-2">{intro}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {stats.map(([I, label, v]) => (
          <div key={label} className="rounded-lg bg-brand-cream/50 border border-brand-gold/20 p-3"><I size={14} className="text-brand-gold" /><div className="font-serif text-[22px] text-brand-ink leading-none mt-1.5">{n(v)}</div><div className="text-[10.5px] text-brand-muted mt-1 leading-tight">{label}</div></div>
        ))}
      </div>
      {extra && <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[12px] text-brand-muted">{extra.map(([l, v]) => <span key={l}>{l}: <strong className="text-brand-ink">{n(v)}</strong></span>)}</div>}
      {error && <div className="mt-2 text-[11.5px] text-brand-orange" data-testid={`${testid}-error`}>Meta non ha restituito alcune metriche: {error}</div>}
      {posts?.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted mb-2">Ultimi {Math.min(posts.length, 12)} post · dati reali</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {posts.slice(0, 12).map((p, i) => (
              <a key={i} href={p.url || "#"} target="_blank" rel="noreferrer" className="group rounded-lg overflow-hidden border border-brand-gold/20 bg-brand-cream/40" data-testid={`${testid}-post-${i}`}>
                {p.image ? <img src={p.image} alt="" className="w-full aspect-square object-cover" loading="lazy" /> : <div className="w-full aspect-square flex items-center justify-center text-[11px] text-brand-muted px-2 text-center line-clamp-4">{p.caption || "(senza testo)"}</div>}
                <div className="px-2 py-1.5 text-[10.5px] text-brand-muted flex items-center justify-between"><span>{p.date}</span><span><Heart size={10} className="inline" /> {n(p.likes)} · <MessageCircle size={10} className="inline" /> {n(p.comments)}</span></div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
